import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/tiktok-platform/components/RequiredMark';
import TiktokSwitch from '@/tiktok-platform/components/TiktokSwitch';
import { nextTemplatePriority } from '@/tiktok-platform/utils/tiktokTemplatePriority';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

/**
 * 从发品页把当前表单另存为产品模板：元信息弹窗（字段/样式对齐属性库保存弹窗）。
 */
export default function SaveProductTemplateMetaModal({
  open,
  shopId,
  categoryId = '',
  productTypeId = '',
  categoryPathLabel = '',
  getFormSnapshot,
  runWithoutUnloadPrompt,
  onClose,
  onSaved,
}) {
  useTranslation('workbench');
  const [name, setName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [remark, setRemark] = useState('');
  const [autoRuleEnabled, setAutoRuleEnabled] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);
  const [priorityLoading, setPriorityLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setStyleNo('');
    setRemark('');
    setAutoRuleEnabled(false);
    setSortOrder(0);
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open || !shopId || !productTypeId) return undefined;
    let cancelled = false;
    setPriorityLoading(true);
    const loadPriority = async () => {
      try {
        const res = await tiktokPlatformAPI.listProductTemplates(shopId, {
          productTypeId,
          page: 1,
          pageSize: 100,
        });
        if (cancelled) return;
        setSortOrder(nextTemplatePriority(res?.items));
      } catch {
        if (!cancelled) setSortOrder(0);
      } finally {
        if (!cancelled) setPriorityLoading(false);
      }
    };
    // 打开弹窗拉优先级时也可能触发工作区跳转；抑制发品页 beforeunload 误报
    if (typeof runWithoutUnloadPrompt === 'function') {
      void runWithoutUnloadPrompt(loadPriority);
    } else {
      void loadPriority();
    }
    return () => { cancelled = true; };
  }, [open, shopId, productTypeId, runWithoutUnloadPrompt]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const submit = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(chartText('saveTpl.needName', fixedUi("请填写模板名称")), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim().slice(0, 128);
    if (!styleNoValue) {
      showToast(chartText('saveTpl.needStyle', fixedUi("请填写款号")), 'error');
      return;
    }
    if (!categoryId || !productTypeId) {
      showToast(chartText('saveTpl.needCategory', fixedUi("缺少适用类目")), 'error');
      return;
    }
    const formSnapshot = typeof getFormSnapshot === 'function' ? getFormSnapshot() : null;
    if (!formSnapshot || typeof formSnapshot !== 'object') {
      showToast(chartText('saveTpl.needContent', fixedUi('无法读取当前商品内容')), 'error');
      return;
    }

    const persist = async () => {
      setSaving(true);
      try {
        await tiktokPlatformAPI.createProductTemplate(shopId, {
          name: title,
          styleNo: styleNoValue,
          remark: String(remark || '').trim() || undefined,
          productTypeId,
          categoryId,
          categoryPathLabel: categoryPathLabel || undefined,
          autoRuleEnabled,
          sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
          payload: {
            form: formSnapshot,
          },
        });
        showToast(chartText('saveTpl.savedProduct', fixedUi('已保存为产品模板')), 'success');
        onSaved?.();
        onClose?.();
      } catch (e) {
        showToast(e?.message || chartText('saveTpl.saveProductFail', fixedUi('保存产品模板失败')), 'error');
      } finally {
        setSaving(false);
      }
    };

    if (typeof runWithoutUnloadPrompt === 'function') {
      await runWithoutUnloadPrompt(persist);
    } else {
      await persist();
    }
  };

  return createPortal(
    <div className="tko-batch-modal tko-attr-tpl-modal" role="presentation">
      <button
        type="button"
        className="tko-batch-modal__backdrop"
        aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="tko-batch-modal__panel tko-attr-tpl-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tko-product-tpl-save-meta-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tko-batch-modal__head">
          <h2 id="tko-product-tpl-save-meta-title">{chartText('saveTpl.saveProduct', fixedUi("保存产品模板"))}</h2>
          <button type="button" className="tko-batch-modal__close" aria-label={chartText('moduleShell.close', fixedUi("关闭"))} disabled={saving} onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit}>
          <div className="tko-batch-modal__body">
            <div className="tko-wizard__form tko-template-create-form">
              <label className="tko-field tko-field--full">
                <span className="tko-field__label">{chartText('productTemplate.colStyleNo', fixedUi("款号"))} <RequiredMark /></span>
                <input
                  className="tko-input"
                  value={styleNo}
                  disabled={saving}
                  maxLength={128}
                  placeholder={chartText('saveTpl.stylePh', fixedUi("请输入款号"))}
                  onChange={(e) => setStyleNo(e.target.value)}
                />
              </label>
              <label className="tko-field tko-field--full">
                <span className="tko-field__label">{chartText('productTemplate.colName', fixedUi("模板名称"))} <RequiredMark /></span>
                <input
                  className="tko-input"
                  value={name}
                  disabled={saving}
                  maxLength={128}
                  placeholder={chartText('saveTpl.namePhProduct', fixedUi('如：女装夏季基础款'))}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="tko-field tko-field--full">
                <span className="tko-field__label">{chartText('productTemplate.colRemark', fixedUi("备注"))}</span>
                <input
                  className="tko-input"
                  value={remark}
                  disabled={saving}
                  maxLength={200}
                  placeholder={chartText('publishSpec.patternOptional', fixedUi("可选"))}
                  onChange={(e) => setRemark(e.target.value)}
                />
              </label>
              <div className="tko-field tko-field--full tko-template-auto-rule">
                <div className="tko-template-auto-rule__switch">
                  <TiktokSwitch
                    checked={autoRuleEnabled}
                    disabled={saving}
                    ariaLabel={chartText('productTemplate.colAutoRule', fixedUi('自动规则'))}
                    onChange={setAutoRuleEnabled}
                  />
                  <span>{chartText('productTemplate.colAutoRule', fixedUi("自动规则"))}</span>
                </div>
                <p className="tko-wizard__inline-hint">
                  {chartText('saveTpl.autoHint', fixedUi('开启后，发品选中该类目时自动带出。优先级仅在同类目内比较，数字越小越优先。'))}
                </p>
                <label className="tko-field">
                  <span className="tko-field__label">{chartText('productTemplate.colPriority', fixedUi("优先级"))}</span>
                  <input
                    className="tko-input"
                    type="number"
                    step={1}
                    value={sortOrder}
                    disabled={saving || priorityLoading}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setSortOrder(Number.isFinite(next) ? next : 0);
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
          <div className="tko-batch-modal__foot">
            <button type="button" className="tko-btn tko-btn--ghost" disabled={saving} onClick={onClose}>{chartText('shop.cancel', fixedUi("取消"))}</button>
            <button type="submit" className="tko-btn tko-btn--primary" disabled={saving}>
              {saving ? chartText('tiktokPublish.saving', fixedUi("保存中…")) : chartText('shop.save', fixedUi("保存"))}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
